import { evaluate } from "mathjs";

import type { LegacyDependencyRule } from "../../../../../types/productDependencies";
import { buildSelectionsScope, resolveFormulaSubcategoryKey } from "../../../../../utils/formulaScope";

/**
 * Helper functions for product selection and dependency management
 */

export function hasColorOptions(materialSchema: any[], productCategory: string, productName: string): boolean {
    const categorySchema = materialSchema.find((schemaItem: any) => schemaItem.Name === productCategory);
    const selectedProductSubcategories =
        categorySchema?.Subcategories?.filter((sub: any) => sub.Name === productName) || [];
    return selectedProductSubcategories.some((sub: any) => sub.Color);
}

export function findSubcategory(
    materialSchema: any[],
    category: string,
    productNameOrId: string,
): { Id?: string; Name?: string; SystemLine?: string; Color?: string } | undefined {
    if (!materialSchema || !Array.isArray(materialSchema)) return undefined;

    const categorySchema = materialSchema.find((schemaItem: any) => {
        const name = (schemaItem.Name || schemaItem.ProductName || schemaItem.name || "").trim().toLowerCase();
        return name === category.trim().toLowerCase();
    });
    if (!categorySchema?.Subcategories) return undefined;

    const key = productNameOrId.trim().toLowerCase();
    return categorySchema.Subcategories.find(
        (sub: any) => sub.Id === productNameOrId || (sub.Name || "").trim().toLowerCase() === key,
    );
}

export function findManufacturerProductId(
    materialSchema: any[],
    category: string,
    productName: string,
): string | undefined {
    if (!materialSchema || !Array.isArray(materialSchema)) {
        return undefined;
    }

    const categorySchema = materialSchema.find((schemaItem: any) => {
        const name = (schemaItem.Name || schemaItem.ProductName || schemaItem.name || "").trim().toLowerCase();
        return name === category.trim().toLowerCase();
    });

    if (!categorySchema || !categorySchema.Subcategories) {
        return undefined;
    }

    const normalizedProductName = productName.trim().toLowerCase();
    const matchingSubs = categorySchema.Subcategories.filter(
        (sub: any) => (sub.Name || "").trim().toLowerCase() === normalizedProductName,
    );

    if (matchingSubs.length === 0) {
        return undefined;
    }

    const defaultSub = matchingSubs.find((sub: any) => !sub.Color);
    return defaultSub?.Id || matchingSubs[0]?.Id;
}

function findParentMapping(
    rule: LegacyDependencyRule | undefined,
    category: string,
    product: string,
    parentManufacturerProductId?: string,
) {
    if (!rule) return undefined;

    return rule.Mappings.find(
        (map) =>
            map.Product === product ||
            map.parentManufacturerProductId === parentManufacturerProductId ||
            (parentManufacturerProductId && map.parentManufacturerProductId === parentManufacturerProductId),
    );
}

/**
 * Recursively sets dependencies for a selected product using API-provided ID mappings.
 */
export function setDependencies(
    dependencies: LegacyDependencyRule[],
    tempStateData: any,
    category: string,
    product: string,
    materialSchema: any[],
    measurements: any[],
    formulas?: any,
    attributes?: any[],
    parentManufacturerProductId?: string,
): void {
    const rule = dependencies.find((dep) => dep.Category === category);
    const parentId =
        parentManufacturerProductId ||
        tempStateData[category]?.manufacturerProductId ||
        findManufacturerProductId(materialSchema, category, product);

    const mapping = findParentMapping(rule, category, product, parentId);
    const deps = mapping?.RequiredProducts || [];

    for (const dep of deps) {
        const formulaKey = dep.formulaProductName || dep.Product;
        const calculatedQuantity = calculateQuantity(
            materialSchema,
            measurements,
            dep.Category,
            formulaKey,
            formulas,
            attributes,
            tempStateData,
        );

        if (calculatedQuantity <= 0) {
            continue;
        }

        const manufacturerProductId =
            dep.manufacturerProductId ||
            findManufacturerProductId(materialSchema, dep.Category, dep.Product);

        const depSub = manufacturerProductId
            ? findSubcategory(materialSchema, dep.Category, manufacturerProductId)
            : findSubcategory(materialSchema, dep.Category, dep.Product);

        tempStateData[dep.Category] = {
            value: dep.Product,
            quantity: calculatedQuantity,
            ...(manufacturerProductId ? { manufacturerProductId } : {}),
            ...(depSub?.SystemLine ? { systemLine: depSub.SystemLine } : {}),
        };

        setDependencies(
            dependencies,
            tempStateData,
            dep.Category,
            dep.Product,
            materialSchema,
            measurements,
            formulas,
            attributes,
            dep.manufacturerProductId,
        );
    }
}

export function calculateQuantity(
    materialSchema: any[],
    measurements: any[],
    category: string,
    productName: string,
    formulas?: any,
    attributes?: any[],
    prefilledValues?: any,
): number {
    if (!formulas) {
        return 1;
    }

    const categoryFormula = formulas[category];
    let expression = categoryFormula?.expression;
    let outputFormat = categoryFormula?.outputFormat;

    if (categoryFormula?.subcategories && productName) {
        const subKey = resolveFormulaSubcategoryKey(category, productName, prefilledValues);
        const subFormula = categoryFormula.subcategories[subKey] || categoryFormula.subcategories[productName];
        if (subFormula) {
            expression = subFormula.expression;
            outputFormat = subFormula.outputFormat;
        }
    }

    if (!expression) {
        return 1;
    }

    try {
        const scope: Record<string, any> = {};

        if (Array.isArray(measurements)) {
            measurements.forEach((m) => {
                const value = m.ActualValue ?? m.CalculatedValue;
                const numVal = Number(value);
                if (!isNaN(numVal) && value !== "" && value !== null && value !== undefined) {
                    scope[m.MeasurementType] = numVal;
                    scope[m.MeasurementType.replace(/\s+/g, "")] = numVal;
                } else {
                    scope[m.MeasurementType] = value;
                    scope[m.MeasurementType.replace(/\s+/g, "")] = value;
                }
            });
        }

        const defaults: Record<string, any> = {
            Area: 0,
            Eave: 0,
            Rake: 0,
            Ridge: 0,
            Hip: 0,
            Valley: 0,
            Headwall: 0,
            Step: 0,
            RollMetal: 0,
            CounterFlashing: 0,
            RoofVent: 0,
            RVs: 0,
            Skylight: 0,
            Skylights: 0,
            Chimney: 0,
            PipeJack: 0,
            Pjack: 0,
            SplitBoots: 0,
            SplitsBoots: 0,
            FurnaceVents: 0,
            Furn: 0,
            BathroomVents: 0,
            BathVents: 0,
            DryerVents: 0,
            GutterLength: 0,
            DownspoutLength: 0,
            SolarPanels: 0,
            Cricket: 0,
            Cooler: 0,
            InstallNewGutters: "false",
            InstallNewDownspouts: "false",
            RemoveExistingGutters: "false",
        };
        Object.entries(defaults).forEach(([key, val]) => {
            if (!(key in scope)) scope[key] = val;
        });

        if (Array.isArray(attributes)) {
            attributes.forEach((attr) => {
                const rawName: string = attr.AttributeName;
                if (!rawName) return;

                const camelKey = rawName.replace(/\s+(\w)/g, (_: string, c: string) => c.toUpperCase());
                const raw = attr.AttributeValue;
                if (raw === null || raw === undefined || raw === "") return;

                let parsed: any;
                try {
                    parsed = JSON.parse(raw);
                } catch {
                    parsed = null;
                }

                let value: any;
                if (Array.isArray(parsed)) {
                    const total = parsed.reduce((sum: number, item: any) => sum + (Number(item.count) || 0), 0);
                    value = total > 0 ? total : parsed.length;
                } else {
                    const num = Number(raw);
                    value = !isNaN(num) && raw !== "" ? num : raw;
                }

                scope[rawName] = value;
                scope[camelKey] = value;
            });
        }

        Object.assign(scope, buildSelectionsScope(prefilledValues));

        if (prefilledValues) {
            Object.entries(prefilledValues).forEach(([prod, data]: [string, any]) => {
                const qty = data.quantity;
                if (typeof qty === "number" && !isNaN(qty)) {
                    scope[prod.replace(/\s+/g, "")] = qty;
                }
            });
        }

        let result = evaluate(expression, scope) as number;

        if (!outputFormat || outputFormat === "WholeNumber") {
            result = Math.ceil(result);
        }
        return isNaN(result) || result === Infinity || result === -Infinity ? 1 : result;
    } catch (error) {
        console.error(`Error calculating quantity for ${category} / ${productName}:`, error);
        return 1;
    }
}

export function deriveColorFromSchema(
    materialSchema: any[],
    manufacturerProductId: string | null | undefined,
    productCategory: string,
): string | undefined {
    if (!manufacturerProductId || !Array.isArray(materialSchema)) {
        return undefined;
    }

    const categoryEntry = materialSchema.find((prod: any) => {
        const name = prod.Name || prod.ProductName || prod.name;
        return name === productCategory;
    });

    const sub = categoryEntry?.Subcategories?.find((s: any) => s.Id === manufacturerProductId && s.Color);
    return sub?.Color as string | undefined;
}
